import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CheckCircle,
  ArrowLeft,
  Mail,
  FileText,
  Layers,
  Loader2,
  AlertCircle,
  Sparkles,
} from 'lucide-react';
import PayTRIframe from '../components/subscription/PayTRIframe';
import {
  getPlans,
  initiatePayment,
  formatPrice,
  type Plan,
  type InitiatePaymentRequest,
} from '../api/payment';

type ModuleType = 'mail' | 'template' | 'bundle';
type TierType = 'standard' | 'pro' | 'unlimited';
type BillingCycle = 'monthly' | 'yearly';

const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // URL'den gelen parametreler
  const preselectedModule = searchParams.get('module') as ModuleType | null;
  const preselectedTier = searchParams.get('tier') as TierType | null;

  // State
  const [plans, setPlans] = useState<{
    mail: Plan[];
    template: Plan[];
    bundle: Plan[];
  } | null>(null);
  const [selectedModule, setSelectedModule] = useState<ModuleType>(
    preselectedModule || 'bundle'
  );
  const [selectedTier, setSelectedTier] = useState<TierType>(
    preselectedTier || 'standard'
  );
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('yearly');
  const [isLoading, setIsLoading] = useState(true);
  const [isInitiatingPayment, setIsInitiatingPayment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentToken, setPaymentToken] = useState<string | null>(null);

  const hasLoadedRef = useRef(false);

  // Planları yükle
  useEffect(() => {
    // Prevent double fetch in React.StrictMode (development)
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const loadPlans = async () => {
      try {
        setIsLoading(true);
        const response = await getPlans();
        setPlans(response.plans);
      } catch (err) {
        setError('Planlar yüklenirken bir hata oluştu');
        console.error('Failed to load plans:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadPlans();
  }, []);

  // Seçili planı bul
  const selectedPlan = plans?.[selectedModule]?.find(
    (p) => p.tier === selectedTier
  );

  // Fiyatı hesapla
  const currentPrice = selectedPlan
    ? billingCycle === 'yearly'
      ? selectedPlan.yearly_price
      : selectedPlan.monthly_price
    : 0;

  // Yıllık tasarruf
  const yearlySavings = selectedPlan
    ? selectedPlan.monthly_price * 12 - selectedPlan.yearly_price * 12
    : 0;

  // Modül ikonları
  const moduleIcons = {
    mail: Mail,
    template: FileText,
    bundle: Layers,
  };

  // Modül isimleri
  const moduleNames = {
    mail: 'E-Posta Modülü',
    template: 'Şablon Modülü',
    bundle: 'Avukasa Ofis',
  };

  // Tier isimleri
  const tierNames = {
    standard: 'Standart',
    pro: 'Profesyonel',
    unlimited: 'Sınırsız',
  };

  // Ödeme başlat
  const handleInitiatePayment = async () => {
    if (!selectedPlan) return;

    try {
      setIsInitiatingPayment(true);
      setError(null);

      const request: InitiatePaymentRequest = {
        module_type: selectedModule,
        tier: selectedTier,
        billing_cycle: billingCycle,
      };

      const response = await initiatePayment(request);
      setPaymentToken(response.iframe_token);
    } catch (err: any) {
      const message =
        err.response?.data?.error?.message || 'Ödeme başlatılamadı';
      setError(message);
      console.error('Failed to initiate payment:', err);
    } finally {
      setIsInitiatingPayment(false);
    }
  };

  // Ödeme başarılı
  const handlePaymentSuccess = () => {
    // Kısa bir bekleme sonrası dashboard'a yönlendir
    setTimeout(() => {
      navigate('/dashboard?payment=success');
    }, 2000);
  };

  // Ödeme hatası
  const handlePaymentError = (message: string) => {
    setError(message);
    setPaymentToken(null);
  };

  // Ödeme iptal
  const handlePaymentClose = () => {
    setPaymentToken(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-cyan-400 animate-spin" />
      </div>
    );
  }

  // Ödeme iframe'i göster
  if (paymentToken) {
    return (
      <div className="min-h-screen bg-slate-950 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <button
            onClick={() => setPaymentToken(null)}
            className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Plan Seçimine Dön
          </button>

          <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">
                Ödeme Bilgileri
              </h2>
              <div className="flex items-center justify-between text-slate-400">
                <span>
                  {selectedPlan?.name} - {tierNames[selectedTier]}
                </span>
                <span className="text-xl font-bold text-cyan-400">
                  {formatPrice(currentPrice)}/ay
                </span>
              </div>
            </div>

            <PayTRIframe
              token={paymentToken}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              onClose={handlePaymentClose}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 py-12">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors mx-auto"
          >
            <ArrowLeft className="w-5 h-5" />
            Geri Dön
          </button>
          <h1 className="text-4xl font-bold text-white mb-4">
            Planınızı Seçin
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            İhtiyaçlarınıza uygun planı seçin ve hemen kullanmaya başlayın.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-8 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-400 max-w-2xl mx-auto">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-slate-900 p-1 rounded-full border border-slate-800 inline-flex relative items-center">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${billingCycle === 'monthly'
                  ? 'bg-slate-800 text-white'
                  : 'text-slate-400 hover:text-white'
                }`}
            >
              Aylık
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${billingCycle === 'yearly'
                  ? 'bg-cyan-500/20 text-cyan-300'
                  : 'text-slate-400 hover:text-white'
                }`}
            >
              Yıllık
              <span className="text-[10px] font-bold bg-cyan-500 text-slate-900 px-2 py-0.5 rounded-full">
                %20 İNDİRİM
              </span>
            </button>
          </div>
        </div>

        {/* Module Selection */}
        <div className="flex justify-center gap-4 mb-8">
          {(Object.keys(moduleNames) as ModuleType[]).map((module) => {
            const Icon = moduleIcons[module];
            return (
              <button
                key={module}
                onClick={() => setSelectedModule(module)}
                className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${selectedModule === module
                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                    : 'bg-slate-900 text-slate-400 border border-slate-800 hover:text-white hover:border-slate-700'
                  }`}
              >
                <Icon className="w-5 h-5" />
                {moduleNames[module]}
              </button>
            );
          })}
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans?.[selectedModule]?.map((plan) => {
            const isSelected = plan.tier === selectedTier;
            const isPro = plan.tier === 'pro';
            const isUnlimited = plan.tier === 'unlimited';
            const price =
              billingCycle === 'yearly'
                ? plan.yearly_price
                : plan.monthly_price;

            return (
              <div
                key={plan.id}
                onClick={() => setSelectedTier(plan.tier as TierType)}
                className={`relative rounded-2xl p-6 border cursor-pointer transition-all ${isSelected
                    ? isUnlimited
                      ? 'bg-gradient-to-b from-purple-900/50 to-slate-900 border-purple-500 ring-2 ring-purple-500/30'
                      : isPro
                        ? 'bg-gradient-to-b from-blue-900/50 to-slate-900 border-blue-500 ring-2 ring-blue-500/30'
                        : 'bg-slate-900 border-cyan-500 ring-2 ring-cyan-500/30'
                    : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'
                  }`}
              >
                {/* Popular Badge */}
                {isPro && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      EN POPÜLER
                    </span>
                  </div>
                )}

                {/* Plan Header */}
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-white mb-1">
                    {tierNames[plan.tier as TierType]}
                  </h3>
                  <p className="text-sm text-slate-400">{plan.description}</p>
                </div>

                {/* Price */}
                <div className="mb-6">
                  <span className="text-4xl font-bold text-white">
                    {formatPrice(price)}
                  </span>
                  <span className="text-slate-400">/ay</span>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {plan.perks.map((perk, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-2 text-sm text-slate-300"
                    >
                      <CheckCircle
                        className={`w-5 h-5 flex-shrink-0 ${isUnlimited
                            ? 'text-purple-400'
                            : isPro
                              ? 'text-blue-400'
                              : 'text-cyan-400'
                          }`}
                      />
                      <span>{perk}</span>
                    </li>
                  ))}
                </ul>

                {/* Selection Indicator */}
                <div
                  className={`w-full py-3 rounded-xl font-medium text-center transition-colors ${isSelected
                      ? isUnlimited
                        ? 'bg-purple-500 text-white'
                        : isPro
                          ? 'bg-blue-500 text-white'
                          : 'bg-cyan-500 text-slate-900'
                      : 'bg-slate-800 text-slate-400'
                    }`}
                >
                  {isSelected ? 'Seçildi' : 'Seç'}
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary & CTA */}
        {selectedPlan && (
          <div className="mt-12 max-w-2xl mx-auto">
            <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    Seçilen Plan
                  </h3>
                  <p className="text-slate-400">
                    {selectedPlan.name} - {tierNames[selectedTier]} (
                    {billingCycle === 'yearly' ? 'Yıllık' : 'Aylık'})
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-cyan-400">
                    {formatPrice(currentPrice)}
                  </div>
                  <div className="text-sm text-slate-400">/ay</div>
                </div>
              </div>

              {billingCycle === 'yearly' && yearlySavings > 0 && (
                <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm">
                  Yıllık ödemede {formatPrice(yearlySavings)} tasarruf
                  ediyorsunuz!
                </div>
              )}

              <button
                onClick={handleInitiatePayment}
                disabled={isInitiatingPayment}
                className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isInitiatingPayment ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Ödeme Hazırlanıyor...
                  </>
                ) : (
                  'Ödemeye Geç'
                )}
              </button>

              <p className="mt-4 text-center text-sm text-slate-500">
                Güvenli ödeme • İstediğiniz zaman iptal edebilirsiniz
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPage;

